"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VariableStore = void 0;
const characterMap_1 = require("./characterMap");
/**
 * Runtime variable store for managing variable values during gameplay
 * Implements session-level caching to reduce database queries
 */
class VariableStore {
    constructor(sessionId, cacheService) {
        this.cacheTimeout = 30 * 60 * 1000; // 30 minutes
        /** In-flight backend persistence tasks (see {@link flushPendingVariablePersistence}). */
        this.pendingPersistenceTasks = new Set();
        /** Tracks whether a batch of NPC ticks has made any real variable changes. */
        this.npcTickBatchActive = false;
        this.npcTickBatchDirty = false;
        this.sessionId = sessionId;
        this.userVariables = new Map(); // Now Map<userId, Map<variableName, VariableValue>>
        this.adventureVariables = new Map();
        this.playerVariables = new Map();
        this.characterVariables = new Map();
        this.sceneVariables = new Map();
        this.lastUpdated = Date.now();
        this.cacheService = cacheService;
        // Generate a unique instance ID for debugging
        this.instanceId = Math.random().toString(36).substr(2, 9);
        // console.log(`🏪 Created VariableStore instance ${this.instanceId} for session ${sessionId}`);
    }
    /**
     * Set the cache service for backend integration
     */
    setCacheService(cacheService) {
        this.cacheService = cacheService;
    }
    /**
     * Mark the start of an NPC tick batch. While active, {@link setVariable} will flag
     * {@link npcTickBatchDirty} when a variable actually changes value/type in its scope.
     * Hosts should call {@link endNpcTickBatch} after finishing all NPC updates.
     */
    beginNpcTickBatch() {
        this.npcTickBatchActive = true;
        this.npcTickBatchDirty = false;
    }
    /**
     * End an NPC tick batch and return whether any variable changed during the batch.
     */
    endNpcTickBatch() {
        const dirty = this.npcTickBatchDirty;
        this.npcTickBatchActive = false;
        this.npcTickBatchDirty = false;
        return dirty;
    }
    /**
     * Get a variable value using precedence lookup
     * Precedence: scene → character → player → adventure → user
     * For stack type variables, returns the top of stack (as string) or undefined if empty
     * For character_map type variables, returns this character's slot string, or "" if missing
     */
    getVariable(variableName, context) {
        // console.log(`🔍 [${this.instanceId}] Getting variable '${variableName}' in context:`, context);
        // Always check in-memory store first (even when cache is expired). Otherwise after the
        // 30-minute cache timeout, getVariable would return undefined and callers like stopTimer
        // would misbehave (e.g. never set stoppedAt because they think the timer was never started).
        // Try in order of precedence: scene → character → player → adventure → user
        // 1. Scene variables (current scene)
        const sceneVars = this.sceneVariables.get(context.currentSceneId);
        if (sceneVars) {
            const sceneVar = sceneVars.get(variableName);
            if (sceneVar !== undefined) {
                return this.unwrapStoredValue(sceneVar, context.characterName);
            }
        }
        // 2. Character variables (current character)
        const characterVars = this.characterVariables.get(context.characterName);
        if (characterVars) {
            const characterVar = characterVars.get(variableName);
            if (characterVar !== undefined) {
                return this.unwrapStoredValue(characterVar, context.characterName);
            }
        }
        // 3. Player variables (current player)
        const playerVars = this.playerVariables.get(context.playerId);
        if (playerVars) {
            const playerVar = playerVars.get(variableName);
            if (playerVar !== undefined) {
                return this.unwrapStoredValue(playerVar, context.characterName);
            }
        }
        // 4. Adventure variables (adventure-wide)
        const adventureVar = this.adventureVariables.get(variableName);
        if (adventureVar !== undefined) {
            return this.unwrapStoredValue(adventureVar, context.characterName);
        }
        // 5. User variables (global user preferences) - keyed by userId
        const userVars = this.userVariables.get(context.userId);
        if (userVars) {
            const userVar = userVars.get(variableName);
            if (userVar !== undefined) {
                return this.unwrapStoredValue(userVar, context.characterName);
            }
        }
        // Not in memory: if cache is expired and we have a backend, try to load (async)
        if (this.cacheService) {
            this.loadVariableFromBackend(variableName, context).catch(console.error);
        }
        // console.log(`🔍 Variable '${variableName}' not found`);
        return undefined;
    }
    /**
     * Returns debug info for all variables in the current context (user, player, character, scene, adventure).
     * Used by the Variables debug scene. Format: "# Scope" headings with "**name:** value" per variable.
     *
     * When `additionalSceneIds` is provided, scene-variable sections are also emitted for those
     * scene ids (e.g. scenes on the return_scene stack while viewing a debug menu scene).
     */
    getVariablesDebugInfo(context, options) {
        const sections = [];
        const formatVar = (name, variable) => {
            let displayValue;
            if (variable.type === 'stack') {
                const stack = this.deserializeStack(variable.value);
                displayValue = stack.length > 0 ? String(stack[stack.length - 1]) : '[]';
            }
            else if (variable.type === 'character_map') {
                displayValue = String(variable.value);
            }
            else {
                displayValue = String(variable.value);
            }
            return `**${name}: ** ${displayValue}`;
        };
        const formatSceneVariablesSection = (sceneId, label) => {
            const sceneVars = this.sceneVariables.get(sceneId);
            if (!sceneVars || sceneVars.size === 0) {
                return `# Scene Variables (${label})\n\n(no variables in store)`;
            }
            const lines = Array.from(sceneVars.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, variable]) => formatVar(name, variable));
            return `# Scene Variables (${label})\n\n${lines.join('\n\n')}`;
        };
        // User variables
        const userVars = this.userVariables.get(context.userId);
        if (userVars && userVars.size > 0) {
            const lines = Array.from(userVars.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, variable]) => formatVar(name, variable));
            sections.push('# User Variables\n\n' + lines.join('\n\n'));
        }
        // Player variables
        const playerVars = this.playerVariables.get(context.playerId);
        if (playerVars && playerVars.size > 0) {
            const lines = Array.from(playerVars.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, variable]) => formatVar(name, variable));
            sections.push('# Player Variables\n\n' + lines.join('\n\n'));
        }
        // Character variables
        const characterVars = this.characterVariables.get(context.characterName);
        if (characterVars && characterVars.size > 0) {
            const lines = Array.from(characterVars.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, variable]) => formatVar(name, variable));
            sections.push('# Character Variables\n\n' + lines.join('\n\n'));
        }
        // Scene variables (current scene)
        const currentSceneVars = this.sceneVariables.get(context.currentSceneId);
        if (currentSceneVars && currentSceneVars.size > 0) {
            sections.push(formatSceneVariablesSection(context.currentSceneId, `current: ${context.currentSceneId}`));
        }
        const additionalSceneIds = options?.additionalSceneIds ?? [];
        for (const sceneId of additionalSceneIds) {
            const trimmed = String(sceneId || '').trim();
            if (!trimmed || trimmed === context.currentSceneId) {
                continue;
            }
            sections.push(formatSceneVariablesSection(trimmed, `return_scene stack: ${trimmed}`));
        }
        // Adventure variables
        if (this.adventureVariables.size > 0) {
            const lines = Array.from(this.adventureVariables.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, variable]) => formatVar(name, variable));
            sections.push('# Adventure Variables\n\n' + lines.join('\n\n'));
        }
        if (sections.length === 0) {
            return 'No variables in cache.';
        }
        return sections.join('\n\n');
    }
    /**
     * Get metadata about a variable including its scope and type
     */
    getVariableMetadata(variableName, context) {
        const scope = this.getVariableScope(variableName, context);
        if (!scope) {
            return undefined;
        }
        const variable = this.getVariableInternal(variableName, context);
        if (!variable) {
            return undefined;
        }
        if (variable.type === 'stack') {
            return {
                scope,
                type: variable.type,
                value: this.deserializeStack(variable.value)
            };
        }
        if (variable.type === 'character_map') {
            return {
                scope,
                type: variable.type,
                value: (0, characterMap_1.deserializeCharacterMap)(variable.value)
            };
        }
        return {
            scope,
            type: variable.type,
            value: variable.value
        };
    }
    /**
     * Validate portrait ID format
     * Portrait IDs can be:
     * 1. Simple format: lowercase alphanumeric with underscores (e.g., "generic", "adventurer_male")
     * 2. Namespaced format: {adventure-id}:{portrait-id} (e.g., "styling-exploration:art_director")
     */
    static isValidPortraitId(portraitId) {
        // Check for namespaced format first
        if (portraitId.includes(':')) {
            const parts = portraitId.split(':');
            if (parts.length !== 2) {
                return false; // Invalid format (multiple colons)
            }
            const [adventureId, localId] = parts;
            // Adventure ID format: lowercase with hyphens
            const adventureIdValid = /^[a-z0-9-]+$/.test(adventureId);
            // Portrait ID format: lowercase with underscores
            const localIdValid = /^[a-z0-9_]+$/.test(localId);
            return adventureIdValid && localIdValid;
        }
        // Simple format: lowercase alphanumeric with underscores
        return /^[a-z0-9_]+$/.test(portraitId);
    }
    /**
     * Set a variable value
     * Requires the variable to be declared unless options.allowUndeclaredPlayer is true (e.g. for invite link payload).
     */
    setVariable(variableName, value, context, variableType, options) {
        // First check if the variable exists and get its scope
        let scope = this.getVariableScope(variableName, context);
        // Allow setting undeclared variables as adventure scope (e.g. sync option coordination)
        if (scope === undefined && options?.allowUndeclaredAdventure) {
            scope = 'adventure';
        }
        // If variable doesn't exist but starts with 'user_', auto-initialize it as a user variable
        // This handles default user variables that should always be available
        if (scope === undefined && variableName.startsWith('user_')) {
            let userVars = this.userVariables.get(context.userId);
            if (!userVars) {
                userVars = new Map();
                this.userVariables.set(context.userId, userVars);
            }
            // Initialize with the provided value (no default needed since we're setting it)
            userVars.set(variableName, {
                name: variableName,
                value: value,
                type: variableType
            });
            scope = 'user';
        }
        // Allow setting undeclared variables as player scope (e.g. from invite link payload)
        if (scope === undefined && options?.allowUndeclaredPlayer) {
            scope = 'player';
        }
        // Auto-initialize mandatory character variables (e.g. character_location, character_last_user_id)
        // when the character doesn't have them yet - can happen when players switch to characters
        // that were added after session creation or when initialization didn't run for that character
        const mandatoryCharacterVars = [
            'character_location',
            'character_last_user_id',
            'character_activities_in_progress',
            'character_activities_completed',
            'character_activity_resume_scenes',
            'character_activities_pending_initialise'
        ];
        if (scope === undefined && mandatoryCharacterVars.includes(variableName)) {
            let characterVars = this.characterVariables.get(context.characterName);
            if (!characterVars) {
                characterVars = new Map();
                this.characterVariables.set(context.characterName, characterVars);
            }
            characterVars.set(variableName, {
                name: variableName,
                value: value,
                type: variableType
            });
            scope = 'character';
        }
        if (scope === undefined) {
            throw new Error(`Variable '${variableName}' is not declared in any scope`);
        }
        const existingForSet = this.getVariableInternal(variableName, context);
        if (existingForSet?.type === 'character_map') {
            throw new Error(`Variable '${variableName}' is a character_map. Use setCharacterMapValue to set this character's slot, or resetCharacterMap to wipe the map.`);
        }
        // Validate user_portrait_id value if it's a string
        if (variableName === 'user_portrait_id' && scope === 'user') {
            if (typeof value !== 'string') {
                throw new Error(`Variable 'user_portrait_id' must be a string, got ${typeof value}`);
            }
            if (!VariableStore.isValidPortraitId(value)) {
                throw new Error(`Invalid portrait ID format: '${value}'. Portrait IDs must be lowercase alphanumeric with underscores, or namespaced format (adventure-id:portrait-id)`);
            }
        }
        const variableValue = {
            name: variableName,
            value,
            type: variableType
        };
        // Update the variable in its correct scope
        switch (scope) {
            case 'scene':
                let sceneVars = this.sceneVariables.get(context.currentSceneId);
                if (!sceneVars) {
                    sceneVars = new Map();
                    this.sceneVariables.set(context.currentSceneId, sceneVars);
                }
                if (this.npcTickBatchActive) {
                    const existing = sceneVars.get(variableName);
                    if (!existing || existing.value !== variableValue.value || existing.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                sceneVars.set(variableName, variableValue);
                // console.log(`🔧 Updated scene variable '${variableName}' in scene '${context.currentSceneId}'`);
                break;
            case 'character':
                let characterVars = this.characterVariables.get(context.characterName);
                if (!characterVars) {
                    characterVars = new Map();
                    this.characterVariables.set(context.characterName, characterVars);
                }
                if (this.npcTickBatchActive) {
                    const existing = characterVars.get(variableName);
                    if (!existing || existing.value !== variableValue.value || existing.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                characterVars.set(variableName, variableValue);
                // console.log(`🔧 Updated character variable '${variableName}' for character '${context.characterName}'`);
                break;
            case 'player':
                let playerVars = this.playerVariables.get(context.playerId);
                if (!playerVars) {
                    playerVars = new Map();
                    this.playerVariables.set(context.playerId, playerVars);
                }
                if (this.npcTickBatchActive) {
                    const existingPlayer = playerVars.get(variableName);
                    if (!existingPlayer || existingPlayer.value !== variableValue.value || existingPlayer.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                playerVars.set(variableName, variableValue);
                // console.log(`🔧 Updated player variable '${variableName}' for player '${context.playerId}'`);
                break;
            case 'adventure':
                if (this.npcTickBatchActive) {
                    const existingAdv = this.adventureVariables.get(variableName);
                    if (!existingAdv || existingAdv.value !== variableValue.value || existingAdv.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                this.adventureVariables.set(variableName, variableValue);
                // console.log(`🔧 Updated adventure variable '${variableName}'`);
                break;
            case 'user':
                let userVars = this.userVariables.get(context.userId);
                if (!userVars) {
                    userVars = new Map();
                    this.userVariables.set(context.userId, userVars);
                }
                if (this.npcTickBatchActive) {
                    const existingUser = userVars.get(variableName);
                    if (!existingUser || existingUser.value !== variableValue.value || existingUser.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                userVars.set(variableName, variableValue);
                // console.log(`🔧 Updated user variable '${variableName}' for userId '${context.userId}'`);
                break;
        }
        this.lastUpdated = Date.now();
        // If cache service is available, persist to backend asynchronously (fire-and-forget)
        if (this.cacheService) {
            const task = this.persistVariableToBackend(variableName, value, variableType, context).catch(console.error);
            this.pendingPersistenceTasks.add(task);
            task.finally(() => this.pendingPersistenceTasks.delete(task));
        }
    }
    /**
     * Await all in-flight variable persistence tasks for this session store.
     * Call after effect replay (e.g. guest-adventure flush) before session switches.
     */
    async flushPendingVariablePersistence() {
        if (this.pendingPersistenceTasks.size === 0) {
            return;
        }
        await Promise.all([...this.pendingPersistenceTasks]);
    }
    scopeIdentifierForPersistence(scope, context) {
        switch (scope) {
            case 'scene':
                return context.currentSceneId;
            case 'character':
                return context.characterName;
            case 'player':
                return context.playerId;
            case 'adventure':
                return this.sessionId;
            case 'user':
                return context.userId;
        }
    }
    /**
     * Check if a variable exists
     */
    hasVariable(variableName, context) {
        // Always check in-memory store (same as getVariable: do not depend on cache validity)
        // 1. Scene variables (current scene)
        const sceneVars = this.sceneVariables.get(context.currentSceneId);
        if (sceneVars && sceneVars.has(variableName)) {
            return true;
        }
        // 2. Character variables (current character)
        const characterVars = this.characterVariables.get(context.characterName);
        if (characterVars && characterVars.has(variableName)) {
            return true;
        }
        // 3. Player variables (current player)
        const playerVars = this.playerVariables.get(context.playerId);
        if (playerVars && playerVars.has(variableName)) {
            return true;
        }
        // 4. Adventure variables (adventure-wide)
        if (this.adventureVariables.has(variableName)) {
            return true;
        }
        // 5. User variables (global user preferences) - keyed by userId
        const userVars = this.userVariables.get(context.userId);
        if (userVars && userVars.has(variableName)) {
            return true;
        }
        return false;
    }
    /**
     * Get the scope where a variable is declared
     * Returns the scope name or undefined if variable doesn't exist
     */
    getVariableScope(variableName, context) {
        // Check in order of precedence: scene → character → player → adventure → user
        // 1. Scene variables (current scene)
        const sceneVars = this.sceneVariables.get(context.currentSceneId);
        if (sceneVars && sceneVars.has(variableName)) {
            return 'scene';
        }
        // 2. Character variables (current character)
        const characterVars = this.characterVariables.get(context.characterName);
        if (characterVars && characterVars.has(variableName)) {
            return 'character';
        }
        // 3. Player variables (current player)
        const playerVars = this.playerVariables.get(context.playerId);
        if (playerVars && playerVars.has(variableName)) {
            return 'player';
        }
        // 4. Adventure variables (adventure-wide)
        if (this.adventureVariables.has(variableName)) {
            return 'adventure';
        }
        // 5. User variables (global user preferences) - keyed by userId
        const userVars = this.userVariables.get(context.userId);
        if (userVars && userVars.has(variableName)) {
            return 'user';
        }
        return undefined;
    }
    /**
     * Load persisted scene-scoped variables for a specific scene from the backend cache service.
     * DB values overwrite in-memory defaults (e.g. conversation history on scenes the player is not on).
     */
    async loadSceneVariablesFromBackend(sceneId, context) {
        if (!this.cacheService) {
            return;
        }
        try {
            const variables = await this.cacheService.getAllVariables(this.sessionId, 'scene', sceneId, context.userId, context.playerId);
            if (!variables || variables.length === 0) {
                return;
            }
            let sceneVars = this.sceneVariables.get(sceneId);
            if (!sceneVars) {
                sceneVars = new Map();
                this.sceneVariables.set(sceneId, sceneVars);
            }
            for (const variable of variables) {
                sceneVars.set(variable.name, variable);
            }
            this.lastUpdated = Date.now();
        }
        catch (error) {
            console.warn(`Failed to load scene variables for scene '${sceneId}':`, error);
        }
    }
    /**
     * Load existing variables from backend cache service
     */
    async loadExistingVariables(context) {
        if (!this.cacheService) {
            return;
        }
        try {
            // Load variables from all scopes
            const scopes = [
                { scope: 'scene', identifier: context.currentSceneId },
                { scope: 'character', identifier: context.characterName },
                { scope: 'player', identifier: context.playerId },
                { scope: 'adventure', identifier: this.sessionId },
                { scope: 'user', identifier: context.userId }
            ];
            for (const { scope, identifier } of scopes) {
                try {
                    // Get all variables for this scope from the database
                    const variables = await this.cacheService.getAllVariables(this.sessionId, scope, identifier, context.userId, context.playerId);
                    if (variables && variables.length > 0) {
                        for (const variable of variables) {
                            // Store in appropriate cache based on scope
                            switch (scope) {
                                case 'scene':
                                    let sceneVars = this.sceneVariables.get(context.currentSceneId);
                                    if (!sceneVars) {
                                        sceneVars = new Map();
                                        this.sceneVariables.set(context.currentSceneId, sceneVars);
                                    }
                                    sceneVars.set(variable.name, variable);
                                    break;
                                case 'character':
                                    let characterVars = this.characterVariables.get(context.characterName);
                                    if (!characterVars) {
                                        characterVars = new Map();
                                        this.characterVariables.set(context.characterName, characterVars);
                                    }
                                    characterVars.set(variable.name, variable);
                                    break;
                                case 'player':
                                    let playerVars = this.playerVariables.get(context.playerId);
                                    if (!playerVars) {
                                        playerVars = new Map();
                                        this.playerVariables.set(context.playerId, playerVars);
                                    }
                                    playerVars.set(variable.name, variable);
                                    break;
                                case 'adventure':
                                    this.adventureVariables.set(variable.name, variable);
                                    break;
                                case 'user':
                                    let userVars = this.userVariables.get(context.userId);
                                    if (!userVars) {
                                        userVars = new Map();
                                        this.userVariables.set(context.userId, userVars);
                                    }
                                    userVars.set(variable.name, variable);
                                    break;
                            }
                        }
                    }
                }
                catch (error) {
                    // Continue loading other scopes even if one fails
                    console.warn(`Failed to load variables for scope ${scope}:`, error);
                }
            }
            this.lastUpdated = Date.now();
        }
        catch (error) {
            console.error('Failed to load existing variables:', error);
        }
    }
    /**
     * Initialize default values for variables
     */
    initializeDefaults(variablesConfig, adventureVariables, characterVariables, sceneVariables, context) {
        // Initialize user variables - keyed by userId
        let userVars = this.userVariables.get(context.userId);
        if (!userVars) {
            userVars = new Map();
            this.userVariables.set(context.userId, userVars);
        }
        for (const variable of variablesConfig.userVariables) {
            if (!userVars.has(variable.name)) {
                const defaultValue = this.getDefaultValue(variable.type, variable.defaultValue);
                userVars.set(variable.name, {
                    name: variable.name,
                    value: defaultValue,
                    type: variable.type
                });
            }
        }
        // Initialize mandatory player variables
        for (const variable of variablesConfig.mandatoryVariables.player) {
            let playerVars = this.playerVariables.get(context.playerId);
            if (!playerVars) {
                playerVars = new Map();
                this.playerVariables.set(context.playerId, playerVars);
            }
            if (!playerVars.has(variable.name)) {
                const defaultValue = this.getDefaultValue(variable.type, variable.defaultValue);
                playerVars.set(variable.name, {
                    name: variable.name,
                    value: defaultValue,
                    type: variable.type
                });
            }
        }
        // Initialize mandatory character variables for all characters
        const characterNames = new Set([context.characterName, ...characterVariables.keys()]);
        for (const characterName of characterNames) {
            let characterVars = this.characterVariables.get(characterName);
            if (!characterVars) {
                characterVars = new Map();
                this.characterVariables.set(characterName, characterVars);
            }
            for (const variable of variablesConfig.mandatoryVariables.character) {
                if (!characterVars.has(variable.name)) {
                    const defaultValue = this.getDefaultValue(variable.type, variable.defaultValue);
                    characterVars.set(variable.name, {
                        name: variable.name,
                        value: defaultValue,
                        type: variable.type
                    });
                }
            }
        }
        // Initialize adventure variables (merge current adventure definition on every init, including rehydration).
        // This ensures new variables added to the adventure (e.g. from sub-adventures) are declared in the store.
        // Existing variables already in the store (e.g. from DB) are left unchanged; variables no longer in the
        // adventure definition are not removed, to avoid data loss when definitions change.
        for (const [name, variable] of adventureVariables) {
            if (!this.adventureVariables.has(name)) {
                this.adventureVariables.set(name, variable);
            }
        }
        // Initialize character variables for ALL characters in the adventure
        // (needed when players can switch characters, e.g. locker room personas)
        for (const [characterName, charVars] of characterVariables) {
            let characterVars = this.characterVariables.get(characterName);
            if (!characterVars) {
                characterVars = new Map();
                this.characterVariables.set(characterName, characterVars);
            }
            for (const [name, variable] of charVars) {
                if (!characterVars.has(name)) {
                    characterVars.set(name, variable);
                }
            }
        }
        // Initialize scene variables
        const sceneVars = sceneVariables.get(context.currentSceneId);
        if (sceneVars) {
            let currentSceneVars = this.sceneVariables.get(context.currentSceneId);
            if (!currentSceneVars) {
                currentSceneVars = new Map();
                this.sceneVariables.set(context.currentSceneId, currentSceneVars);
            }
            for (const [name, variable] of sceneVars) {
                if (!currentSceneVars.has(name)) {
                    currentSceneVars.set(name, variable);
                }
            }
        }
        // Variables are now initialized in memory
        // They will be persisted to the database when they are first accessed or modified
    }
    /**
     * Ensure character-scoped defaults exist for {@code characterName} without overwriting values.
     * Used when binding open-character instances (`Musician#2`) that were not present at session start.
     */
    ensureCharacterVariableDefaults(characterName, defaults) {
        let characterVars = this.characterVariables.get(characterName);
        if (!characterVars) {
            characterVars = new Map();
            this.characterVariables.set(characterName, characterVars);
        }
        for (const [name, variable] of defaults) {
            if (!characterVars.has(name)) {
                characterVars.set(name, { ...variable });
            }
        }
        this.lastUpdated = Date.now();
    }
    /**
     * Load a variable from backend asynchronously (fire-and-forget)
     */
    async loadVariableFromBackend(variableName, context) {
        if (!this.cacheService)
            return;
        try {
            // Try each scope in precedence order
            const scopes = [
                { scope: 'scene', identifier: context.currentSceneId },
                { scope: 'character', identifier: context.characterName },
                { scope: 'player', identifier: context.playerId },
                { scope: 'adventure', identifier: this.sessionId },
                { scope: 'user', identifier: context.userId }
            ];
            for (const { scope, identifier } of scopes) {
                const value = await this.cacheService.getVariable(this.sessionId, scope, identifier, variableName, context.userId, context.playerId);
                if (value !== undefined) {
                    // Store in the same scope we found it in so lookups use correct context (e.g. user_player_name per userId)
                    const variable = {
                        name: value.name,
                        value: value.value,
                        type: value.type
                    };
                    switch (scope) {
                        case 'scene':
                            let sceneVars = this.sceneVariables.get(identifier);
                            if (!sceneVars) {
                                sceneVars = new Map();
                                this.sceneVariables.set(identifier, sceneVars);
                            }
                            sceneVars.set(variableName, variable);
                            break;
                        case 'character':
                            let characterVars = this.characterVariables.get(identifier);
                            if (!characterVars) {
                                characterVars = new Map();
                                this.characterVariables.set(identifier, characterVars);
                            }
                            characterVars.set(variableName, variable);
                            break;
                        case 'player':
                            let playerVars = this.playerVariables.get(identifier);
                            if (!playerVars) {
                                playerVars = new Map();
                                this.playerVariables.set(identifier, playerVars);
                            }
                            playerVars.set(variableName, variable);
                            break;
                        case 'adventure':
                            this.adventureVariables.set(variableName, variable);
                            break;
                        case 'user':
                            let userVars = this.userVariables.get(identifier);
                            if (!userVars) {
                                userVars = new Map();
                                this.userVariables.set(identifier, userVars);
                            }
                            userVars.set(variableName, variable);
                            break;
                    }
                    this.lastUpdated = Date.now();
                    return;
                }
            }
        }
        catch (error) {
            console.error('Failed to load variable from backend:', error);
        }
    }
    /**
     * Persist a variable to backend asynchronously (fire-and-forget)
     * Only persists variables that are explicitly set, not variables that are just referenced during interpolation
     */
    async persistVariableToBackend(variableName, value, variableType, context) {
        if (!this.cacheService) {
            return;
        }
        try {
            // Prefer the in-memory scope (where setVariable just wrote). A DB-first lookup can
            // pick a stale row in a lower-precedence scope (e.g. adventure) after declarations
            // move to character scope, causing reloads to read an empty character default instead.
            let foundScope = null;
            const memoryScope = this.getVariableScope(variableName, context);
            if (memoryScope) {
                foundScope = {
                    scope: memoryScope,
                    identifier: this.scopeIdentifierForPersistence(memoryScope, context)
                };
            }
            else {
                const scopes = [
                    { scope: 'scene', identifier: context.currentSceneId },
                    { scope: 'character', identifier: context.characterName },
                    { scope: 'player', identifier: context.playerId },
                    { scope: 'adventure', identifier: this.sessionId },
                    { scope: 'user', identifier: context.userId }
                ];
                for (const { scope, identifier } of scopes) {
                    const existingValue = await this.cacheService.getVariable(this.sessionId, scope, identifier, variableName, context.userId, context.playerId);
                    if (existingValue !== undefined) {
                        foundScope = { scope, identifier };
                        break;
                    }
                }
            }
            // Persist the variable if we found it in database or memory
            if (foundScope) {
                await this.cacheService.setVariable(this.sessionId, foundScope.scope, foundScope.identifier, variableName, value, variableType, context.userId, context.playerId);
            }
            else {
                // Variable doesn't exist in any scope - don't persist it
                console.warn(`Variable '${variableName}' not found in any scope during interpolation - not persisting`);
            }
        }
        catch (error) {
            console.error('Failed to persist variable to backend:', error);
        }
    }
    /**
     * Check if cache is still valid
     */
    isCacheValid() {
        return Date.now() - this.lastUpdated < this.cacheTimeout;
    }
    /**
     * Clear the cache
     */
    clearCache() {
        this.userVariables.clear();
        this.adventureVariables.clear();
        this.playerVariables.clear();
        this.characterVariables.clear();
        this.sceneVariables.clear();
        this.lastUpdated = Date.now();
    }
    /** Export all in-memory variable scopes for runtime snapshots. */
    exportAllScopes() {
        return {
            user: VariableStore.mapOfMapsToRecord(this.userVariables),
            adventure: VariableStore.mapToRecord(this.adventureVariables),
            player: VariableStore.mapOfMapsToRecord(this.playerVariables),
            character: VariableStore.mapOfMapsToRecord(this.characterVariables),
            scene: VariableStore.mapOfMapsToRecord(this.sceneVariables),
        };
    }
    /** Replace all in-memory variable scopes from a runtime snapshot. */
    importAllScopes(data) {
        this.clearCache();
        this.userVariables = VariableStore.recordToMapOfMaps(data.user);
        this.adventureVariables = VariableStore.recordToMap(data.adventure);
        this.playerVariables = VariableStore.recordToMapOfMaps(data.player);
        this.characterVariables = VariableStore.recordToMapOfMaps(data.character);
        this.sceneVariables = VariableStore.recordToMapOfMaps(data.scene);
        this.lastUpdated = Date.now();
    }
    static mapToRecord(map) {
        const record = {};
        for (const [key, value] of map.entries()) {
            record[key] = { ...value };
        }
        return record;
    }
    static recordToMap(record) {
        const map = new Map();
        for (const [key, value] of Object.entries(record)) {
            map.set(key, { ...value });
        }
        return map;
    }
    static mapOfMapsToRecord(map) {
        const record = {};
        for (const [outerKey, innerMap] of map.entries()) {
            record[outerKey] = VariableStore.mapToRecord(innerMap);
        }
        return record;
    }
    static recordToMapOfMaps(record) {
        const map = new Map();
        for (const [outerKey, innerRecord] of Object.entries(record)) {
            map.set(outerKey, VariableStore.recordToMap(innerRecord));
        }
        return map;
    }
    /**
     * For testing only: makes the in-memory cache appear expired (isCacheValid() returns false)
     * without clearing data. Use to verify that getVariable still returns in-memory values when
     * the cache is expired (e.g. after 30 minutes), so that stopTimer and similar callers work.
     */
    simulateCacheExpiryForTesting() {
        this.lastUpdated = Date.now() - this.cacheTimeout - 1;
    }
    /**
     * Get cache statistics
     */
    getCacheStats() {
        const totalSize = this.userVariables.size +
            this.adventureVariables.size +
            Array.from(this.playerVariables.values()).reduce((sum, vars) => sum + vars.size, 0) +
            Array.from(this.characterVariables.values()).reduce((sum, vars) => sum + vars.size, 0) +
            Array.from(this.sceneVariables.values()).reduce((sum, vars) => sum + vars.size, 0);
        return {
            size: totalSize,
            isValid: this.isCacheValid(),
            lastUpdated: this.lastUpdated
        };
    }
    /**
     * Initialize scene variables for a scene (called at game start)
     * This is used to initialize all scene variables from the adventure definition
     */
    initializeSceneVariables(sceneId, variables) {
        for (const [name, variable] of variables) {
            let sceneVars = this.sceneVariables.get(sceneId);
            if (!sceneVars) {
                sceneVars = new Map();
                this.sceneVariables.set(sceneId, sceneVars);
            }
            if (!sceneVars.has(name)) {
                sceneVars.set(name, variable);
            }
        }
        this.lastUpdated = Date.now();
    }
    /**
     * Check if Jest environment is torn down
     */
    isJestEnvironmentTornDown() {
        // Check if we're in a Jest environment that's been torn down
        return process.env.NODE_ENV === 'test' && !process.env.JEST_WORKER_ID;
    }
    /**
     * Helper method to determine variable type from a value
     * This centralizes the type determination logic used by both ScriptEvaluator and backend services
     */
    static inferVariableType(value) {
        if (typeof value === 'string') {
            return 'string';
        }
        else if (typeof value === 'number') {
            return 'number';
        }
        else if (typeof value === 'boolean') {
            return 'boolean';
        }
        else {
            throw new Error(`Invalid variable type: ${typeof value}`);
        }
    }
    /**
     * Helper method to build context for variable operations
     * This standardizes context creation across the codebase
     */
    static buildVariableContext(playerId, characterName, currentSceneId, userId, timeZoneOffsetMinutes) {
        return {
            playerId,
            characterName,
            currentSceneId,
            userId,
            timeZoneOffsetMinutes
        };
    }
    /**
     * Get the entire stack array for a stack variable
     */
    getStackVariable(variableName, context) {
        const variable = this.getVariableInternal(variableName, context);
        if (!variable || variable.type !== 'stack') {
            return undefined;
        }
        return this.deserializeStack(variable.value);
    }
    /**
     * Push a value onto a stack variable
     */
    pushStack(variableName, value, context) {
        // First check if the variable exists and get its scope
        let scope = this.getVariableScope(variableName, context);
        // If variable doesn't exist, try to auto-initialize it as a player variable
        // (for mandatory stack variables like return_scene that should be player-scoped)
        if (scope === undefined) {
            // For known mandatory player stack variables, initialize in player scope
            // This handles the case where pushStack is called before initializeDefaults
            const knownMandatoryPlayerStackVars = ['return_scene', 'opening_session_id'];
            if (knownMandatoryPlayerStackVars.includes(variableName)) {
                let playerVars = this.playerVariables.get(context.playerId);
                if (!playerVars) {
                    playerVars = new Map();
                    this.playerVariables.set(context.playerId, playerVars);
                }
                // Initialize as empty stack
                playerVars.set(variableName, {
                    name: variableName,
                    value: this.serializeStack([]),
                    type: 'stack'
                });
                scope = 'player';
            }
            else {
                throw new Error(`Variable '${variableName}' is not declared in any scope`);
            }
        }
        // Get current stack or initialize as empty array
        const currentVariable = this.getVariableInternal(variableName, context);
        const currentStack = currentVariable && currentVariable.type === 'stack'
            ? this.deserializeStack(currentVariable.value)
            : [];
        // Push the new value
        const newStack = [...currentStack, value];
        const serializedStack = this.serializeStack(newStack);
        // Update the variable
        const variableValue = {
            name: variableName,
            value: serializedStack,
            type: 'stack'
        };
        // Update the variable in its correct scope
        switch (scope) {
            case 'scene':
                let sceneVars = this.sceneVariables.get(context.currentSceneId);
                if (!sceneVars) {
                    sceneVars = new Map();
                    this.sceneVariables.set(context.currentSceneId, sceneVars);
                }
                sceneVars.set(variableName, variableValue);
                break;
            case 'character':
                let characterVars = this.characterVariables.get(context.characterName);
                if (!characterVars) {
                    characterVars = new Map();
                    this.characterVariables.set(context.characterName, characterVars);
                }
                characterVars.set(variableName, variableValue);
                break;
            case 'player':
                let playerVars = this.playerVariables.get(context.playerId);
                if (!playerVars) {
                    playerVars = new Map();
                    this.playerVariables.set(context.playerId, playerVars);
                }
                playerVars.set(variableName, variableValue);
                break;
            case 'adventure':
                this.adventureVariables.set(variableName, variableValue);
                break;
            case 'user':
                let userVarsForPush = this.userVariables.get(context.userId);
                if (!userVarsForPush) {
                    userVarsForPush = new Map();
                    this.userVariables.set(context.userId, userVarsForPush);
                }
                userVarsForPush.set(variableName, variableValue);
                break;
        }
        this.lastUpdated = Date.now();
        // Persist to backend
        if (this.cacheService) {
            this.persistVariableToBackend(variableName, serializedStack, 'stack', context).catch(console.error);
        }
    }
    /**
     * Pop a value from a stack variable
     */
    popStack(variableName, context) {
        // First check if the variable exists and get its scope
        let scope = this.getVariableScope(variableName, context);
        if (scope === undefined) {
            throw new Error(`Variable '${variableName}' is not declared in any scope`);
        }
        // Get current stack
        const currentVariable = this.getVariableInternal(variableName, context);
        if (!currentVariable || currentVariable.type !== 'stack') {
            throw new Error(`Variable '${variableName}' is not a stack type`);
        }
        const currentStack = this.deserializeStack(currentVariable.value);
        if (currentStack.length === 0) {
            throw new Error(`Cannot pop from empty stack '${variableName}'`);
        }
        // Pop the last value
        const poppedValue = currentStack.pop();
        const newStack = currentStack;
        const serializedStack = this.serializeStack(newStack);
        // Update the variable
        const variableValue = {
            name: variableName,
            value: serializedStack,
            type: 'stack'
        };
        // Update the variable in its correct scope
        switch (scope) {
            case 'scene':
                let sceneVars = this.sceneVariables.get(context.currentSceneId);
                if (!sceneVars) {
                    sceneVars = new Map();
                    this.sceneVariables.set(context.currentSceneId, sceneVars);
                }
                sceneVars.set(variableName, variableValue);
                break;
            case 'character':
                let characterVars = this.characterVariables.get(context.characterName);
                if (!characterVars) {
                    characterVars = new Map();
                    this.characterVariables.set(context.characterName, characterVars);
                }
                characterVars.set(variableName, variableValue);
                break;
            case 'player':
                let playerVars = this.playerVariables.get(context.playerId);
                if (!playerVars) {
                    playerVars = new Map();
                    this.playerVariables.set(context.playerId, playerVars);
                }
                playerVars.set(variableName, variableValue);
                break;
            case 'adventure':
                this.adventureVariables.set(variableName, variableValue);
                break;
            case 'user':
                let userVarsForPush = this.userVariables.get(context.userId);
                if (!userVarsForPush) {
                    userVarsForPush = new Map();
                    this.userVariables.set(context.userId, userVarsForPush);
                }
                userVarsForPush.set(variableName, variableValue);
                break;
        }
        this.lastUpdated = Date.now();
        // Persist to backend
        if (this.cacheService) {
            this.persistVariableToBackend(variableName, serializedStack, 'stack', context).catch(console.error);
        }
        return poppedValue;
    }
    /**
     * Peek at the top of a stack variable without popping
     */
    peekStack(variableName, context) {
        const variable = this.getVariableInternal(variableName, context);
        if (!variable || variable.type !== 'stack') {
            return undefined;
        }
        const stack = this.deserializeStack(variable.value);
        return stack.length > 0 ? stack[stack.length - 1] : undefined;
    }
    /**
     * Whole map for a character_map variable.
     */
    getCharacterMap(variableName, context) {
        const variable = this.requireCharacterMapVariable(variableName, context);
        return (0, characterMap_1.deserializeCharacterMap)(variable.value);
    }
    /**
     * Current character's slot; "" if the key is missing.
     */
    getCharacterMapValue(variableName, context) {
        const map = this.getCharacterMap(variableName, context);
        const key = context.characterName;
        if (!key || map[key] === undefined) {
            return '';
        }
        return map[key];
    }
    /**
     * Merge a slot write for the acting character. Empty or clear deletes the key.
     */
    setCharacterMapSlot(variableName, slotValue, context, options) {
        const characterKey = (context.characterName ?? '').trim();
        if (!characterKey) {
            throw new Error(`Cannot write character_map '${variableName}': current character name is empty`);
        }
        const scope = this.getVariableScope(variableName, context);
        if (scope === undefined) {
            throw new Error(`Variable '${variableName}' is not declared in any scope`);
        }
        this.requireCharacterMapVariable(variableName, context);
        const map = (0, characterMap_1.applyCharacterMapSlot)((0, characterMap_1.deserializeCharacterMap)(this.getVariableInternal(variableName, context)?.value ?? '{}'), characterKey, slotValue, options);
        this.writeCharacterMap(variableName, map, scope, context);
    }
    /**
     * Admin wipe: replace the entire map with {}.
     */
    resetCharacterMap(variableName, context) {
        const scope = this.getVariableScope(variableName, context);
        if (scope === undefined) {
            throw new Error(`Variable '${variableName}' is not declared in any scope`);
        }
        this.requireCharacterMapVariable(variableName, context);
        this.writeCharacterMap(variableName, {}, scope, context);
    }
    requireCharacterMapVariable(variableName, context) {
        const variable = this.getVariableInternal(variableName, context);
        if (!variable) {
            throw new Error(`Variable '${variableName}' is not declared in any scope`);
        }
        if (variable.type !== 'character_map') {
            throw new Error(`Variable '${variableName}' is not a character_map`);
        }
        return variable;
    }
    writeCharacterMap(variableName, map, scope, context) {
        const serialized = (0, characterMap_1.serializeCharacterMap)(map);
        const variableValue = {
            name: variableName,
            value: serialized,
            type: 'character_map'
        };
        switch (scope) {
            case 'scene': {
                let sceneVars = this.sceneVariables.get(context.currentSceneId);
                if (!sceneVars) {
                    sceneVars = new Map();
                    this.sceneVariables.set(context.currentSceneId, sceneVars);
                }
                if (this.npcTickBatchActive) {
                    const existing = sceneVars.get(variableName);
                    if (!existing || existing.value !== variableValue.value || existing.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                sceneVars.set(variableName, variableValue);
                break;
            }
            case 'character': {
                let characterVars = this.characterVariables.get(context.characterName);
                if (!characterVars) {
                    characterVars = new Map();
                    this.characterVariables.set(context.characterName, characterVars);
                }
                if (this.npcTickBatchActive) {
                    const existing = characterVars.get(variableName);
                    if (!existing || existing.value !== variableValue.value || existing.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                characterVars.set(variableName, variableValue);
                break;
            }
            case 'player': {
                let playerVars = this.playerVariables.get(context.playerId);
                if (!playerVars) {
                    playerVars = new Map();
                    this.playerVariables.set(context.playerId, playerVars);
                }
                if (this.npcTickBatchActive) {
                    const existing = playerVars.get(variableName);
                    if (!existing || existing.value !== variableValue.value || existing.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                playerVars.set(variableName, variableValue);
                break;
            }
            case 'adventure': {
                if (this.npcTickBatchActive) {
                    const existingAdv = this.adventureVariables.get(variableName);
                    if (!existingAdv || existingAdv.value !== variableValue.value || existingAdv.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                this.adventureVariables.set(variableName, variableValue);
                break;
            }
            case 'user': {
                let userVars = this.userVariables.get(context.userId);
                if (!userVars) {
                    userVars = new Map();
                    this.userVariables.set(context.userId, userVars);
                }
                if (this.npcTickBatchActive) {
                    const existingUser = userVars.get(variableName);
                    if (!existingUser || existingUser.value !== variableValue.value || existingUser.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                userVars.set(variableName, variableValue);
                break;
            }
        }
        this.lastUpdated = Date.now();
        if (this.cacheService) {
            this.persistVariableToBackend(variableName, serialized, 'character_map', context).catch(console.error);
        }
    }
    unwrapStoredValue(variable, characterName) {
        if (variable.type === 'stack') {
            return this.getStackTop(variable.value);
        }
        if (variable.type === 'character_map') {
            return (0, characterMap_1.getCharacterMapSlot)((0, characterMap_1.deserializeCharacterMap)(variable.value), characterName);
        }
        return variable.value;
    }
    /**
     * Internal method to get variable value object (not deserialized)
     */
    getVariableInternal(variableName, context) {
        // Always read in-memory values (same as getVariable / hasVariable) so metadata and
        // user-input updates still work after the session cache TTL expires.
        // Try in order of precedence: scene → character → player → adventure → user
        const sceneVars = this.sceneVariables.get(context.currentSceneId);
        if (sceneVars) {
            const sceneVar = sceneVars.get(variableName);
            if (sceneVar !== undefined)
                return sceneVar;
        }
        const characterVars = this.characterVariables.get(context.characterName);
        if (characterVars) {
            const characterVar = characterVars.get(variableName);
            if (characterVar !== undefined)
                return characterVar;
        }
        const playerVars = this.playerVariables.get(context.playerId);
        if (playerVars) {
            const playerVar = playerVars.get(variableName);
            if (playerVar !== undefined)
                return playerVar;
        }
        const adventureVar = this.adventureVariables.get(variableName);
        if (adventureVar !== undefined)
            return adventureVar;
        // User variables are keyed by userId
        const userVars = this.userVariables.get(context.userId);
        if (userVars) {
            const userVar = userVars.get(variableName);
            if (userVar !== undefined)
                return userVar;
        }
        return undefined;
    }
    /**
     * Helper to get top of stack (for getVariable)
     */
    getStackTop(serializedStack) {
        const stack = this.deserializeStack(serializedStack);
        return stack.length > 0 ? stack[stack.length - 1] : undefined;
    }
    /**
     * Serialize stack array to JSON string
     */
    serializeStack(stack) {
        return JSON.stringify(stack);
    }
    /**
     * Deserialize JSON string to stack array
     */
    deserializeStack(serializedStack) {
        try {
            const parsed = JSON.parse(serializedStack);
            if (!Array.isArray(parsed)) {
                throw new Error('Stack value must be a JSON array');
            }
            if (parsed.some(item => typeof item !== 'string')) {
                throw new Error('Stack must contain only strings');
            }
            return parsed;
        }
        catch (error) {
            // If deserialization fails, return empty array
            console.warn(`Failed to deserialize stack: ${error}`);
            return [];
        }
    }
    /**
     * Resolves the runtime value for a declared variable default (or type fallback).
     */
    static resolveDefaultValue(type, defaultValue) {
        if (defaultValue !== undefined) {
            if (type === 'stack') {
                if (typeof defaultValue === 'string' && defaultValue.trim().startsWith('[')) {
                    return defaultValue;
                }
                return '[]';
            }
            if (type === 'character_map') {
                if (typeof defaultValue === 'string' && defaultValue.trim().startsWith('{')) {
                    return defaultValue;
                }
                return '{}';
            }
            return defaultValue;
        }
        switch (type) {
            case 'stack':
                return '[]';
            case 'character_map':
                return '{}';
            case 'number':
                return 0;
            case 'boolean':
                return false;
            case 'string':
            default:
                return '';
        }
    }
    /**
     * Writes a declared variable back to its default in adventure or character scope.
     * Used by {@code resetVariableToDefault} script helpers.
     */
    resetVariableToDeclaredDefault(variableName, context, declaration) {
        const value = VariableStore.resolveDefaultValue(declaration.type, declaration.defaultValue);
        const variableValue = {
            name: variableName,
            value,
            type: declaration.type
        };
        const persistContext = declaration.scope === 'character' && declaration.characterName
            ? { ...context, characterName: declaration.characterName }
            : context;
        switch (declaration.scope) {
            case 'adventure':
                if (this.npcTickBatchActive) {
                    const existingAdv = this.adventureVariables.get(variableName);
                    if (!existingAdv || existingAdv.value !== variableValue.value || existingAdv.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                this.adventureVariables.set(variableName, variableValue);
                break;
            case 'character': {
                const targetCharacter = declaration.characterName || context.characterName;
                let characterVars = this.characterVariables.get(targetCharacter);
                if (!characterVars) {
                    characterVars = new Map();
                    this.characterVariables.set(targetCharacter, characterVars);
                }
                if (this.npcTickBatchActive) {
                    const existing = characterVars.get(variableName);
                    if (!existing || existing.value !== variableValue.value || existing.type !== variableValue.type) {
                        this.npcTickBatchDirty = true;
                    }
                }
                characterVars.set(variableName, variableValue);
                break;
            }
        }
        this.lastUpdated = Date.now();
        if (this.cacheService) {
            this.persistVariableToBackend(variableName, value, declaration.type, persistContext).catch(console.error);
        }
    }
    /**
     * Get default value for a variable type
     */
    getDefaultValue(type, defaultValue) {
        return VariableStore.resolveDefaultValue(type, defaultValue);
    }
    /**
     * Set user portrait ID with validation
     * This is a convenience method that validates the portrait ID before setting it
     * @param portraitId The portrait ID to set (must be valid format)
     * @param context The variable context
     * @throws Error if portrait ID is invalid
     */
    setUserPortrait(portraitId, context) {
        // Validate portrait ID format
        if (!VariableStore.isValidPortraitId(portraitId)) {
            throw new Error(`Invalid portrait ID format: '${portraitId}'. Portrait IDs must be lowercase alphanumeric with underscores, or namespaced format (adventure-id:portrait-id)`);
        }
        // Set the variable using the standard setVariable method
        // This will also validate that user_portrait_id exists and is a user variable
        this.setVariable('user_portrait_id', portraitId, context, 'string');
    }
}
exports.VariableStore = VariableStore;
//# sourceMappingURL=VariableStore.js.map